import { Component } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { PRODUCT_HEROES } from '../product-hero.data';
import { ProductShots, Shot } from '../product-shots';

// Promo landing page for the Color Picker VS Code extension, served at
// /plugins/vscode/color-picker. SEO is applied by the NavigationInterceptor
// from navigation.data.ts (client) and seo-route-meta.js (server). The copy
// describes only what the extension actually does: eyedrop, the color under
// the cursor and Insert, conversions across every color space, WCAG and APCA
// checks, harmonies, tints, color vision simulations and recents. Reuses the
// Chrome picker page styles.
@Component( {
  selector: 'app-vscode-color-picker-page',
  templateUrl: './vscode-color-picker-page.component.html',
  styleUrls: [ '../chrome-color-picker-page/chrome-color-picker-page.component.scss', '../macos-tidy-desktop-page/macos-tidy-desktop-page.component.scss', '../product-page.scss' ],
} )
export class VscodeColorPickerPageComponent extends ProductShots {

  readonly marketplaceUrl = 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-color-picker';
  // the same extension for Cursor, Antigravity and VSCodium
  readonly openVsxUrl = 'https://open-vsx.org/extension/codigrate/cod-color-picker';
  // The product on its stage at the top of the page (product-hero.data.ts), and the facts as
  // chips under the pitch.
  readonly hero = PRODUCT_HEROES[ 'vscode-color-picker' ];
  readonly facts = [ 'Free', 'VS Code, Cursor, Antigravity', 'Every VS Code Based Editor' ];


  readonly features = [
    {
      title: 'Eyedrop Anywhere',
      description: 'Sample any pixel on your screen. Your code, a design tool, an image, anything you can see.',
    },
    {
      title: 'Works With Your Code',
      description: 'Put the cursor on a hex color and it appears in the panel, one click away. Insert writes the current color back into your code.',
    },
    {
      title: 'Every Color Space',
      description: 'Hex, RGB, HSL, HSV, OKLCH, CIELAB, CMYK and more, plus copy-ready CSS rules, each one click to copy.',
    },
    {
      title: 'Accessibility Checks',
      description: 'WCAG 2 contrast ratio and APCA of the color as text on white, black and its complementary, with AA and AAA verdicts.',
    },
    {
      title: 'Harmony, Tints and Tones',
      description: 'Every classic scheme from the color wheel, with the color stepped toward white, black and gray.',
    },
    {
      title: 'Color Vision Deficiency',
      description: 'The color beside its simulation under protan, deutan, tritan and monochromacy vision, from mild to severe.',
    },
    {
      title: 'Matches Your Theme',
      description: 'The panel takes its colors from the active editor theme, light, dark or high contrast.',
    },
    {
      title: 'Recent Colors',
      description: 'Your last picks stay in the panel, so the shades you are working with are always one click away.',
    },
  ];

  // The first three things it does under the product's picture, the rest further down (the
  // Apps & Plugins layout, ../product-page.scss).
  readonly highlights = this.features.slice( 0, 3 );
  readonly more = this.features.slice( 3 );

  // Its store picture (the Chrome and JetBrains pickers' store style), under Screenshots; it
  // opens in the lightbox at full size.
  readonly shots: Shot[] = [
    {
      name: 'Grab Any Color',
      src: './assets/product-lines/tools/vscode-color-picker/screenshot-grab-any-color.webp',
      large: './assets/product-lines/tools/vscode-color-picker/screenshot-grab-any-color-large.webp',
      alt: 'Grab any color, right in VS Code: the Color Picker side bar panel with the color under the cursor, recent colors and every color space for a picked indigo',
    },
  ];

  constructor( private analyticsService: AnalyticsService ) {
    super();
  }

  onInstall( store: 'vs-marketplace' | 'open-vsx' ): void {
    this.analyticsService.logEvent( 'Extension Install Clicked', { extension: 'vscode-color-picker', store } );
  }
}
